// Phase 2 — compliance notification bell. Count badge + dropdown feed
// backed by /api/notifications. Brand-only; best-effort polling. Carries
// no compliance logic — it renders what the signal engine produced.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { badgeLabel } from '@/lib/notificationBadge';
import { TOOLTIP_DELAY_MS } from './chromeControls';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SEV_DOT = {
  critical: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-sky-500',
};

export default function NotificationBell() {
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const loadCount = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/notifications/count`, { credentials: 'include' });
      if (r.ok) setUnread((await r.json()).unread || 0);
    } catch { /* badge is best-effort */ }
  }, []);

  const loadList = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/notifications?limit=20`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setItems(d.notifications || []);
        setUnread(d.unread || 0);
      }
    } catch { /* best-effort */ }
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 60000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  const markRead = async (id) => {
    setItems((xs) => xs.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await fetch(`${API}/api/notifications/${id}/read`, { method: 'POST', credentials: 'include' });
    } catch { /* optimistic */ }
  };

  const markAllRead = async () => {
    setItems((xs) => xs.map((n) => ({ ...n, read: true })));
    setUnread(0);
    try {
      await fetch(`${API}/api/notifications/read-all`, { method: 'POST', credentials: 'include' });
    } catch { /* optimistic */ }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
                data-testid="notification-bell"
                className="relative flex items-center justify-center w-7 h-7 rounded-full bg-card shadow-sm hover:bg-muted/60 transition-colors"
              >
                <Bell className="w-3.5 h-3.5 text-gray-700" strokeWidth={1.5} />
                {unread > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none tabular-nums ring-2 ring-white"
                        data-testid="notification-badge">
                    {badgeLabel(unread)}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Notifications</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent align="end" sideOffset={10} className="w-96 rounded-2xl p-0 shadow-xl border-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <p className="text-sm font-semibold text-foreground">Compliance alerts</p>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-sky-600 hover:text-sky-700 flex items-center gap-1"
                    data-testid="notification-mark-all">
              <Check className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">You're all caught up.</p>
          ) : (
            items.map((n) => (
              <Link
                key={n.id}
                to={n.link || '#'}
                onClick={() => { markRead(n.id); setOpen(false); }}
                className={`block px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 ${n.read ? 'opacity-60' : ''}`}
                data-testid={`notification-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {/* Severity was conveyed by dot color alone — give it an
                      accessible name so it isn't color-only (WCAG 1.4.1). */}
                  <span
                    role="img"
                    aria-label={`${n.severity || 'normal'} severity`}
                    className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${SEV_DOT[n.severity] || 'bg-gray-400'}`}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{n.title}</p>
                    <p className="text-xs text-muted-foreground">{n.body}</p>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
